import { useState, forwardRef, useImperativeHandle } from 'react';
import { IconCheck, IconTrashXFilled, IconX } from '@tabler/icons-react';
import ModalEliminarItemInventario from './ModalEliminarItemInventario';

const FONT = '"Public Sans", sans-serif';

const isActionColumn = (col) => col.key === 'editar' || col.key === 'eliminar';

const numericKeys = ['current_quantity', 'minimum_stock', 'maximum_stock'];

function cellTextStyle(col, row) {
  if (col.key === 'name') {
    return { fontSize: '15px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.92)' };
  }
  if (numericKeys.includes(col.key)) {
    const low =
      col.key === 'current_quantity' &&
      row.current_quantity != null &&
      row.minimum_stock != null &&
      row.current_quantity <= row.minimum_stock;
    return {
      fontSize: '15px',
      fontWeight: 600,
      color: low ? '#f87c2b' : 'rgba(255, 255, 255, 0.85)',
      fontVariantNumeric: 'tabular-nums',
    };
  }
  if (col.key === 'supplier') {
    return { fontSize: '14px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.6)' };
  }
  return { fontSize: '14px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.6)' };
}

const TablaInventario = forwardRef(function TablaInventario(
  { columns, rows, selectable = false, onDelete, emptyComponent },
  ref
) {
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState([]);
  const [pendingNames, setPendingNames] = useState([]);

  useImperativeHandle(ref, () => ({
    clearSelection: () => setSelected([]),
  }));

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Al filtrar la tabla, las filas ocultas no deben contar ni borrarse.
  const activeSelected = selected.filter((id) => rows.some((row) => row.id === id));

  const openBulkModal = () => {
    const items = rows.filter((row) => activeSelected.includes(row.id));
    setPendingIds(activeSelected.slice());
    setPendingNames(items.map((row) => row.name));
    setModalOpen(true);
  };

  const confirmBulkDelete = () => {
    if (onDelete) onDelete([...pendingIds]);
    setSelected([]);
    setPendingIds([]);
    setPendingNames([]);
    setModalOpen(false);
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`flex-1 min-h-0 overflow-auto pedidos-scroll ${activeSelected.length > 0 ? 'pb-24' : ''}`}>
        <div style={{ minWidth: rows.length > 0 ? '940px' : undefined }}>
          {/* Header */}
          <div
            className="sticky top-0 z-10 flex items-center bg-black"
            style={{ padding: '0 12px', height: '40px' }}
          >
            {selectable && <div style={{ width: '48px', flexShrink: 0 }} />}
            {columns.map((col, i) => (
              <span
                key={i}
                className={`flex items-center ${isActionColumn(col) ? 'justify-center' : 'justify-start'}`}
                style={{
                  flex: col.flex || 1,
                  minWidth: col.minWidth || 0,
                  fontSize: '11px',
                  lineHeight: '16px',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  fontFamily: FONT,
                  color: 'rgba(255, 255, 255, 0.35)',
                }}
              >
                {col.label}
              </span>
            ))}
          </div>

          {/* Rows */}
          {rows.length === 0 && emptyComponent ? (
            <div className="flex items-center justify-center px-4 py-10" style={{ minHeight: '360px' }}>
              {emptyComponent}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {rows.map((row) => {
                const isSelected = selected.includes(row.id);

                return (
                  <div
                    key={row.id}
                    className={`flex items-center rounded-2xl transition-colors duration-200 ease-out ${
                      isSelected ? '' : '[@media(hover:hover)]:hover:bg-white/[0.03]'
                    }`}
                    style={{
                      padding: '0 12px',
                      minHeight: '64px',
                      backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
                    }}
                  >
                    {selectable && (
                      <button
                        type="button"
                        onClick={() => toggle(row.id)}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={`Seleccionar ${row.name || row.id}`}
                        className="flex items-center justify-center shrink-0 rounded-full border-none bg-transparent cursor-pointer touch-manipulation"
                        style={{ width: '44px', height: '44px', marginRight: '4px' }}
                      >
                        <span
                          className="flex items-center justify-center rounded-full transition-colors duration-200"
                          style={{
                            width: '22px',
                            height: '22px',
                            backgroundColor: isSelected ? '#0071e3' : 'rgba(255, 255, 255, 0.08)',
                          }}
                        >
                          {isSelected && <IconCheck size={13} color="#fff" stroke={3} />}
                        </span>
                      </button>
                    )}
                    {columns.map((col, j) => (
                      <span
                        key={j}
                        className={`flex items-center min-w-0 ${
                          isActionColumn(col) ? 'justify-center' : 'justify-start'
                        }`}
                        style={{
                          flex: col.flex || 1,
                          minWidth: col.minWidth || 0,
                          fontFamily: FONT,
                          ...cellTextStyle(col, row),
                        }}
                      >
                        {col.render ? (
                          col.render(row)
                        ) : col.badge ? (
                          <span
                            className="inline-flex items-center justify-center"
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.06)',
                              borderRadius: '9999px',
                              height: '30px',
                              padding: '0 12px',
                              fontSize: '13px',
                              fontWeight: 600,
                              fontFamily: FONT,
                              color: 'rgba(255, 255, 255, 0.8)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row[col.key]}
                          </span>
                        ) : (
                          <span className="truncate" title={row[col.key] ? String(row[col.key]) : undefined}>
                            {row[col.key]}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Selection action bar */}
      {activeSelected.length > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full"
          style={{
            maxWidth: 'calc(100vw - 24px)',
            padding: '8px',
            backgroundColor: 'rgba(20, 20, 20, 0.94)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            boxShadow: '0 20px 48px rgba(0, 0, 0, 0.6)',
            fontFamily: FONT,
          }}
        >
          <span
            className="shrink-0 truncate"
            style={{
              padding: '0 10px',
              fontSize: '13px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.9)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {activeSelected.length === 1 ? '1 seleccionado' : `${activeSelected.length} seleccionados`}
          </span>
          <button
            type="button"
            onClick={() => setSelected([])}
            className="flex items-center justify-center gap-1.5 shrink-0 h-11 px-4 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.1]"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: FONT,
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <IconX size={15} color="rgba(255, 255, 255, 0.6)" stroke={2} />
            Deseleccionar
          </button>
          <button
            type="button"
            onClick={openBulkModal}
            className="flex items-center justify-center gap-1.5 shrink-0 h-11 px-4 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:brightness-110"
            style={{
              backgroundColor: '#ff453a',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: FONT,
            }}
          >
            <IconTrashXFilled size={15} color="#fff" />
            Eliminar
          </button>
        </div>
      )}

      <ModalEliminarItemInventario
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setPendingIds([]); setPendingNames([]); }}
        onConfirm={confirmBulkDelete}
        itemName={pendingNames[0] || ''}
        count={pendingIds.length}
      />
    </div>
  );
});

export default TablaInventario;
